import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

type PropertyDetail = {
  id: string;
  name: string;
  role: 'owner' | 'member' | null;
  defaultMowCadenceDays: number;
  mapImageUrl: string | null;
  mapImageWidth: number | null;
  mapImageHeight: number | null;
  geometryVerifiedAt: string | null;
  archivedAt: string | null;
  archivedReason: string | null;
};

type UserSummary = { id: string; email: string; displayName: string };

export const Route = createFileRoute('/app/$propertyId')({
  component: PropertyLayout,
});

function PropertyLayout() {
  const { propertyId } = Route.useParams();

  const { data, isPending, error } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () =>
      api.get<{ property: PropertyDetail; ownerSummary: UserSummary; memberCount: number }>(
        '/v1/properties/' + propertyId,
      ),
  });

  if (isPending) {
    return <p className="text-muted-foreground p-6">Loading…</p>;
  }

  if (error || !data) {
    return <p className="text-destructive p-6">Failed to load property.</p>;
  }

  const { property } = data;
  const isOwner = property.role === 'owner';

  const navLink = 'text-sm text-muted-foreground hover:text-foreground transition-colors';
  const tabLink = 'shrink-0 whitespace-nowrap border-b-2 px-1 pb-2 text-sm transition-colors';
  const tabLinkActive = { className: 'border-primary text-foreground font-medium' };
  const tabLinkInactive = { className: 'border-transparent text-muted-foreground hover:text-foreground' };

  return (
    <div>
      <div className="border-b border-border bg-background px-6 pt-2">
        <div className="flex items-center gap-4 flex-wrap text-sm">
          <Link to="/app/dashboard" className={navLink}>
            ← Properties
          </Link>
          <span className="text-muted-foreground">/</span>
          <span className="font-medium">{property.name}</span>
          {property.role && (
            <span className="text-xs text-muted-foreground capitalize border border-border rounded px-1.5 py-0.5">
              {property.role}
            </span>
          )}
        </div>
        <div className="-mb-px flex items-center gap-4 overflow-x-auto pt-3">
          <Link
            to="/app/$propertyId"
            params={{ propertyId }}
            activeOptions={{ exact: true }}
            className={tabLink}
            activeProps={tabLinkActive}
            inactiveProps={tabLinkInactive}
          >
            Dashboard
          </Link>
          <Link
            to="/app/$propertyId/zones"
            params={{ propertyId }}
            className={tabLink}
            activeProps={tabLinkActive}
            inactiveProps={tabLinkInactive}
          >
            Zones
          </Link>
          <Link
            to="/app/$propertyId/map"
            params={{ propertyId }}
            className={tabLink}
            activeProps={tabLinkActive}
            inactiveProps={tabLinkInactive}
          >
            Map
          </Link>
          <Link
            to="/app/$propertyId/members"
            params={{ propertyId }}
            className={tabLink}
            activeProps={tabLinkActive}
            inactiveProps={tabLinkInactive}
          >
            Members
          </Link>
          <Link
            to="/app/$propertyId/equipment"
            params={{ propertyId }}
            className={tabLink}
            activeProps={tabLinkActive}
            inactiveProps={tabLinkInactive}
          >
            Equipment
          </Link>
          {isOwner && (
            <Link
              to="/app/$propertyId/settings"
              params={{ propertyId }}
              className={tabLink}
              activeProps={tabLinkActive}
              inactiveProps={tabLinkInactive}
            >
              Settings
            </Link>
          )}
        </div>
      </div>
      <Outlet />
    </div>
  );
}
